import { fieldTypes } from '../constants/app';
import { ILabel, IValidationRule } from '../constants/interfaces';
import { IFormStateValue } from './formUtils';

interface ConfirmationRule {
  condition: string;
  canContinue?: boolean;
  message: string;
  shouldReset?: boolean;
  title?: string;
  field?: number[];
}

function normalizeActionRequiredRules(
  rules: ConfirmationRule | ConfirmationRule[] | null | undefined
): ConfirmationRule[] {
  if (rules == null) return [];
  if (Array.isArray(rules)) return rules;
  return [rules];
}

/**
 * Collects all answer values for a label id from top-level form state and from
 * summation / child_row_list row payloads (JSON `response` strings).
 */
export function collectAllValuesForLabel(
  labelId: number,
  formState: Record<string, IFormStateValue>,
  labels?: ILabel[]
): string[] {
  const idStr = String(labelId);
  const out: string[] = [];
  const direct = formState[idStr]?.value;
  if (direct !== undefined && direct !== null && String(direct).trim() !== '') {
    out.push(String(direct));
  }
  if (!labels?.length) return out;

  for (const field of labels) {
    if (
      field.type !== fieldTypes.summation &&
      field.type !== fieldTypes.childRowList
    ) {
      continue;
    }
    const block = formState[String(field.labelId)];
    const children = block?.children;
    if (!Array.isArray(children)) continue;

    for (const rowGroup of children) {
      if (!Array.isArray(rowGroup)) continue;
      for (const cell of rowGroup) {
        if (!cell || typeof cell !== 'object') continue;
        const raw = (cell as { response?: unknown }).response;
        if (typeof raw !== 'string' || !raw.trim()) continue;
        try {
          const parsed = JSON.parse(raw) as Record<string, unknown>;
          if (!parsed || typeof parsed !== 'object') continue;
          if (Object.prototype.hasOwnProperty.call(parsed, idStr)) {
            out.push(String(parsed[idStr] ?? ''));
          }
        } catch {
          /* ignore malformed row JSON */
        }
      }
    }
  }
  return out;
}

/**
 * Matches `value319 == 'No'` / `value333 == "NO"` style conditions from servicer_request_types.rules.
 */
function tryEvaluateSimpleValueEquality(
  condition: string,
  formState: Record<string, IFormStateValue>,
  labels?: ILabel[]
): boolean | null {
  const t = condition.trim();
  const patterns = [
    /^value(\d+)\s*==\s*'([^']*)'$/i,
    /^value(\d+)\s*==\s*"([^"]*)"$/i,
  ];
  for (const p of patterns) {
    const m = t.match(p);
    if (m) {
      const id = parseInt(m[1], 10);
      const expected = m[2];
      const vals = collectAllValuesForLabel(id, formState, labels);
      const exp = expected.trim().toLowerCase();
      return vals.some((v) => String(v).trim().toLowerCase() === exp);
    }
  }
  return null;
}

export function validateConfirmationRules(
  formState: Record<string, IFormStateValue>,
  rules: ConfirmationRule | ConfirmationRule[] | null | undefined = [],
  labels?: ILabel[]
): { isValid: boolean; error: IValidationRule } {
  let error: IValidationRule = {
    canContinue: false,
    message: 'Validation error',
    shouldReset: false,
  };
  let isValid = true;

  const ruleList = normalizeActionRequiredRules(rules);

  for (const rule of ruleList) {
    const simple = tryEvaluateSimpleValueEquality(
      rule.condition,
      formState,
      labels
    );

    let triggered: boolean;
    if (simple !== null) {
      triggered = simple;
    } else {
      const variableNames = Array.from(
        rule.condition.matchAll(/\bvalue\d+\b/g)
      ).map((m) => m[0]);
      const argList = variableNames;
      const values = argList.map((name) => {
        const fieldNum = parseInt(name.replace('value', ''), 10);
        const vals = collectAllValuesForLabel(fieldNum, formState, labels);
        const first =
          vals.find((v) => String(v).trim() !== '') ??
          formState[String(fieldNum)]?.value;
        return first ?? '';
      });

      try {
        // eslint-disable-next-line no-new-func
        const fn = new Function(...argList, `return ${rule.condition};`);
        const result = fn(...values);
        triggered = Boolean(result);
      } catch (err) {
        console.error('Error evaluating condition:', err);
        triggered = false;
      }
    }

    if (triggered) {
      isValid = false;
      error = {
        canContinue: rule.canContinue ?? false,
        message: rule.message,
        shouldReset: rule.shouldReset ?? false,
        title: rule.title,
      };
      break;
    }
  }

  return { error, isValid };
}
